import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import TraderModal from "../features/traders/components/TraderModal";

const AppDialogContext = createContext(null);

function normalizeDialogConfig(defaultTitle, input) {
  if (typeof input === "string") {
    return {
      title: defaultTitle,
      message: input
    };
  }

  return {
    title: input?.title || defaultTitle,
    message: input?.message || "",
    confirmLabel: input?.confirmLabel,
    cancelLabel: input?.cancelLabel,
    placeholder: input?.placeholder,
    defaultValue: input?.defaultValue,
    inputLabel: input?.inputLabel,
    width: input?.width,
    tone: input?.tone
  };
}

function DialogBody({ dialog, onResolve }) {
  const [inputValue, setInputValue] = useState(dialog.defaultValue || "");
  const toneColor = dialog.tone === "danger" ? "#b91c1c" : "#0f766e";
  const toneBackground = dialog.tone === "danger" ? "#fef2f2" : "#f0fdfa";
  const toneBorder = dialog.tone === "danger" ? "#fecaca" : "#99f6e4";

  const renderActions = () => {
    if (dialog.type === "alert") {
      return (
        <button
          type="button"
          onClick={() => onResolve(undefined)}
          style={{
            minWidth: "120px",
            height: "42px",
            padding: "0 16px",
            borderRadius: "12px",
            border: "1px solid #0f766e",
            background: "#0f766e",
            color: "#ffffff",
            fontWeight: 700,
            cursor: "pointer"
          }}
        >
          {dialog.confirmLabel || "OK"}
        </button>
      );
    }

    if (dialog.type === "confirm") {
      return (
        <>
          <button
            type="button"
            onClick={() => onResolve(false)}
            style={{
              minWidth: "120px",
              height: "42px",
              padding: "0 16px",
              borderRadius: "12px",
              border: "1px solid #cbd5e1",
              background: "#ffffff",
              color: "#334155",
              fontWeight: 700,
              cursor: "pointer"
            }}
          >
            {dialog.cancelLabel || "Cancel"}
          </button>
          <button
            type="button"
            onClick={() => onResolve(true)}
            style={{
              minWidth: "120px",
              height: "42px",
              padding: "0 16px",
              borderRadius: "12px",
              border: `1px solid ${toneColor}`,
              background: toneColor,
              color: "#ffffff",
              fontWeight: 700,
              cursor: "pointer"
            }}
          >
            {dialog.confirmLabel || "Confirm"}
          </button>
        </>
      );
    }

    return (
      <form
        onSubmit={(event) => {
          event.preventDefault();
          onResolve(inputValue);
        }}
        style={{ display: "contents" }}
      >
        <button
          type="button"
          onClick={() => onResolve(null)}
          className="app-button-base"
          style={{
            minWidth: "120px",
            borderRadius: "12px",
            border: "1px solid #cbd5e1",
            background: "#ffffff",
            color: "#334155",
            cursor: "pointer"
          }}
        >
          {dialog.cancelLabel || "Cancel"}
        </button>
        <button
          type="submit"
          className="app-button-base"
          style={{
            minWidth: "120px",
            borderRadius: "12px",
            border: `1px solid ${toneColor}`,
            background: toneColor,
            color: "#ffffff",
            cursor: "pointer"
          }}
        >
          {dialog.confirmLabel || "Continue"}
        </button>
      </form>
    );
  };

  return (
    <div style={{ display: "grid", gap: "18px" }}>
      <div
        style={{
          padding: "14px 16px",
          borderRadius: "14px",
          border: `1px solid ${toneBorder}`,
          background: toneBackground,
          color: "#334155"
        }}
      >
        <div style={{ fontSize: "13px", fontWeight: 700, color: toneColor, marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.04em" }}>
          {dialog.type === "prompt" ? "Input required" : dialog.type === "confirm" ? "Please confirm" : "Message"}
        </div>
        <div style={{ margin: 0, lineHeight: 1.6, whiteSpace: "pre-line" }}>{dialog.message}</div>
      </div>

      {dialog.type === "prompt" ? (
        <label style={{ display: "grid", gap: "8px", fontSize: "13px", fontWeight: 700, color: "#334155" }}>
          <span>{dialog.inputLabel || "Value"}</span>
          <input
            type="text"
            value={inputValue}
            autoFocus
            placeholder={dialog.placeholder || ""}
            onChange={(event) => setInputValue(event.target.value)}
            style={{
              height: "44px",
              borderRadius: "12px",
              border: "1px solid #cbd5e1",
              padding: "0 14px",
              boxSizing: "border-box",
              color: "#0f172a"
            }}
          />
        </label>
      ) : null}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", flexWrap: "wrap" }}>
        {renderActions()}
      </div>
    </div>
  );
}

export function AppDialogProvider({ children }) {
  const [dialog, setDialog] = useState(null);
  const pendingResolverRef = useRef(null);

  const resolveDialog = useCallback((value) => {
    const resolve = pendingResolverRef.current;
    pendingResolverRef.current = null;
    setDialog(null);

    if (resolve) {
      resolve(value);
    }
  }, []);

  const openDialog = useCallback((type, config) => {
    if (pendingResolverRef.current) {
      pendingResolverRef.current(type === "confirm" ? false : null);
      pendingResolverRef.current = null;
    }

    return new Promise((resolve) => {
      pendingResolverRef.current = resolve;
      setDialog({
        type,
        ...config
      });
    });
  }, []);

  const contextValue = useMemo(() => ({
    showAlert: (input) => openDialog("alert", normalizeDialogConfig("Notice", input)),
    showConfirm: (input) => openDialog("confirm", normalizeDialogConfig("Confirm action", input)),
    showPrompt: (input) => openDialog("prompt", normalizeDialogConfig("Enter a value", input))
  }), [openDialog]);

  return (
    <AppDialogContext.Provider value={contextValue}>
      {children}
      <TraderModal
        isOpen={Boolean(dialog)}
        onClose={() => resolveDialog(dialog?.type === "confirm" ? false : null)}
        title={dialog?.title || "Notice"}
        width={dialog?.width || "520px"}
      >
        {dialog ? <DialogBody dialog={dialog} onResolve={resolveDialog} /> : null}
      </TraderModal>
    </AppDialogContext.Provider>
  );
}

export function useAppDialog() {
  const context = useContext(AppDialogContext);

  if (!context) {
    throw new Error("useAppDialog must be used within an AppDialogProvider");
  }

  return context;
}